import { CalendarPlus, ChevronLeft, ChevronRight, Filter, Star } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type DragEvent, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router';

import { AdminPage } from '@/components/admin/AdminPage';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/empty-state';
import { FilterPills } from '@/components/ui/filter-pills';
import { useClients } from '@/hooks/clients';
import { useJobsInRange } from '@/hooks/jobs';
import { api, ApiError } from '@/lib/api';
import { addDays, addMonths, monthGrid, sameMonth, startOfMonth, weekRange } from '@/lib/calendar';
import { formatDay, formatDuration, formatWallTime } from '@/lib/dates';
import { todayInBusinessZone } from '@shared/dates';
import { useToast } from '@/lib/toast-context';
import { cn } from '@/lib/utils';
import { JobCreateDialog } from '@/pages/admin/jobs/JobCreateDialog';
import { RescheduleDialog } from '@/pages/admin/jobs/JobActionDialogs';
import { JOB_STATUS_LABELS, type JobListItem, type JobStatusValue } from '@shared/schemas/job';
import { WEEKDAY_LABELS } from '@shared/schemas/client';
import { MAX_PAGE_SIZE } from '@shared/schemas/pagination';

/*
 * /admin/calendar — ROADMAP.md §4.7.
 *
 * **It fetches the visible range and nothing else.** P8 is the prototype
 * pulling `limit=1000` for every view; here the month grid asks for its 42
 * days, the week for its 7, and the server refuses anything over two months.
 *
 * Rescheduling has two paths to the same server call, deliberately:
 * drag a chip onto a day, or open the chip and use the Reschedule dialog —
 * drag alone is a control a whole group of users cannot operate, the same
 * rule as the checklist reorder in 3.2. Completed and cancelled jobs are not
 * draggable; the server would refuse, so the handle does not offer.
 */

type View = 'month' | 'week' | 'day' | 'agenda';

/*
 * Written out per status rather than built from a template string, because
 * Tailwind only generates classes it can see in the source — a constructed
 * `bg-status-${tone}/15` compiles to nothing and the chips would render
 * unstyled. The legend dots reuse the same record.
 */
const CHIP_CLASSES: Record<JobStatusValue, string> = {
  pending: 'bg-status-pending/15 text-status-pending-text',
  scheduled: 'bg-status-scheduled/15 text-status-scheduled-text',
  in_progress: 'bg-status-in-progress/15 text-status-in-progress-text',
  completed: 'bg-status-completed/15 text-status-completed-text',
  cancelled: 'bg-status-cancelled/15 text-status-cancelled-text',
};

const LEGEND_DOT_CLASSES: Record<JobStatusValue, string> = {
  pending: 'bg-status-pending',
  scheduled: 'bg-status-scheduled',
  in_progress: 'bg-status-in-progress',
  completed: 'bg-status-completed',
  cancelled: 'bg-status-cancelled',
};

const VIEW_OPTIONS: { value: View; label: string }[] = [
  { value: 'month', label: 'Month' },
  { value: 'week', label: 'Week' },
  { value: 'day', label: 'Day' },
  { value: 'agenda', label: 'Agenda' },
];

/** Agenda shows two weeks from its anchor. */
const AGENDA_DAYS = 14;

function rangeFor(view: View, anchor: string): { from: string; to: string; days: string[] } {
  if (view === 'month') return monthGrid(anchor);
  if (view === 'week') return weekRange(anchor);
  if (view === 'day') return { from: anchor, to: anchor, days: [anchor] };
  const to = addDays(anchor, AGENDA_DAYS - 1);
  return { from: anchor, to, days: [] };
}

function stepAnchor(view: View, anchor: string, direction: 1 | -1): string {
  if (view === 'month') return addMonths(anchor, direction);
  if (view === 'week') return addDays(anchor, 7 * direction);
  if (view === 'day') return addDays(anchor, direction);
  return addDays(anchor, AGENDA_DAYS * direction);
}

function rangeTitle(view: View, anchor: string, from: string, to: string): string {
  if (view === 'month') {
    const first = startOfMonth(anchor);
    return formatDay(first).slice(2);
  }
  if (view === 'day') return formatDay(anchor);
  return `${formatDay(from)} – ${formatDay(to)}`;
}

export function CalendarPage() {
  const [view, setView] = useState<View>('month');
  const [anchor, setAnchor] = useState(todayInBusinessZone());
  const [creating, setCreating] = useState<{ date: string; time?: string } | null>(null);
  const [rescheduling, setRescheduling] = useState<JobListItem | null>(null);
  const [clientId, setClientId] = useState('');
  const [highPriorityOnly, setHighPriorityOnly] = useState(false);

  const { from, to, days } = rangeFor(view, anchor);
  const jobs = useJobsInRange(from, to, {
    ...(clientId === '' ? {} : { clientId }),
    ...(highPriorityOnly ? { highPriorityOnly: true } : {}),
  });

  /*
   * The client picker's options. Capped at the list endpoint's ceiling, which
   * for a business with a few dozen customers is nowhere near — and this is a
   * convenience filter rather than the only route to a client's work, which is
   * their own record.
   */
  const clients = useClients({
    sort: 'name',
    page: 1,
    search: '',
    activity: 'active',
    highPriorityOnly: false,
    pageSize: MAX_PAGE_SIZE,
  });
  const today = todayInBusinessZone();

  const byDay = new Map<string, JobListItem[]>();
  for (const job of jobs.data?.data ?? []) {
    const list = byDay.get(job.scheduledDate) ?? [];
    list.push(job);
    byDay.set(job.scheduledDate, list);
  }

  return (
    <AdminPage
      title="Calendar"
      description="Jobs by day. Drag one to another day, or open it to reschedule by keyboard."
      actions={
        <Button
          onClick={() => {
            setCreating({ date: today });
          }}
        >
          <CalendarPlus aria-hidden="true" />
          Create job
        </Button>
      }
    >
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            aria-label="Earlier"
            onClick={() => {
              setAnchor(stepAnchor(view, anchor, -1));
            }}
          >
            <ChevronLeft aria-hidden="true" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            aria-label="Later"
            onClick={() => {
              setAnchor(stepAnchor(view, anchor, 1));
            }}
          >
            <ChevronRight aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setAnchor(today);
            }}
          >
            Today
          </Button>
          <h2 aria-live="polite" className="ml-1 text-h3 text-ink-900">
            {rangeTitle(view, anchor, from, to)}
          </h2>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/*
            **Filtered in SQL, not on the screen.** The calendar fetches the
            window it can see and nothing more (P8) — a filter applied after the
            fetch would still pull every client's month to show one client's.
          */}
          <label className="sr-only" htmlFor="calendar-client">
            Filter by client
          </label>
          <select
            id="calendar-client"
            value={clientId}
            onChange={(event) => {
              setClientId(event.target.value);
            }}
            className="min-h-touch rounded-md border border-ink-300 bg-white px-2.5 text-label text-ink-900"
          >
            <option value="">All clients</option>
            {(clients.data?.data ?? []).map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.displayName}
              </option>
            ))}
          </select>

          <button
            type="button"
            role="switch"
            aria-checked={highPriorityOnly}
            onClick={() => {
              setHighPriorityOnly((current) => !current);
            }}
            className={cn(
              'inline-flex min-h-touch items-center gap-1.5 rounded-full px-3.5 text-label',
              highPriorityOnly
                ? 'bg-honey-400 text-ink-900'
                : 'border border-ink-300 bg-white text-ink-700 hover:bg-honey-50',
            )}
          >
            <Star
              aria-hidden="true"
              className={cn('size-3.5', highPriorityOnly && 'fill-bark-900')}
            />
            High priority only
          </button>

          <FilterPills
            label="Calendar view"
            options={VIEW_OPTIONS}
            value={view}
            onChange={setView}
          />
        </div>
      </div>

      <Legend />

      {/*
        **Says what is hidden, rather than leaving an empty week to be read as a
        quiet one.** A filtered calendar that looks identical to an unfiltered
        one is how somebody concludes there is no work on Thursday.
      */}
      {clientId === '' && !highPriorityOnly ? null : (
        <p className="mb-3 flex flex-wrap items-center gap-2 rounded-sm border border-ink-300 bg-honey-50 px-3 py-2 text-meta text-bark-900">
          <Filter aria-hidden="true" className="size-4 shrink-0" />
          Showing part of the calendar
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setClientId('');
              setHighPriorityOnly(false);
            }}
          >
            Show everything
          </Button>
        </p>
      )}

      {jobs.isError ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            title="The calendar could not be loaded"
            onRetry={() => {
              void jobs.refetch();
            }}
          />
        </div>
      ) : jobs.isPending ? (
        <div aria-busy="true">
          <span className="sr-only">Loading the calendar</span>
          <div aria-hidden="true" className="h-96 animate-pulse rounded-lg bg-white" />
        </div>
      ) : view === 'agenda' ? (
        <AgendaView
          from={from}
          to={to}
          byDay={byDay}
          onCreate={(date) => {
            setCreating({ date });
          }}
          onReschedule={setRescheduling}
        />
      ) : view === 'day' ? (
        <DayView
          day={anchor}
          jobs={byDay.get(anchor) ?? []}
          onCreate={(date, time) => {
            setCreating({ date, time });
          }}
          onReschedule={setRescheduling}
        />
      ) : (
        <GridView
          view={view}
          days={days}
          anchor={anchor}
          today={today}
          byDay={byDay}
          onCreate={(date) => {
            setCreating({ date });
          }}
          onReschedule={setRescheduling}
        />
      )}

      <JobCreateDialog
        key={creating === null ? 'closed' : `${creating.date}-${creating.time ?? ''}`}
        open={creating !== null}
        onOpenChange={(next) => {
          if (!next) setCreating(null);
        }}
        initialDate={creating?.date}
        initialTime={creating?.time}
      />

      {rescheduling ? (
        <RescheduleDialog
          key={rescheduling.id}
          job={rescheduling}
          open
          onOpenChange={(next) => {
            if (!next) setRescheduling(null);
          }}
        />
      ) : null}
    </AdminPage>
  );
}

/** The prototype's status colours, named — plus the high-priority star. */
function Legend() {
  return (
    <ul className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
      {(Object.keys(JOB_STATUS_LABELS) as JobStatusValue[]).map((status) => (
        <li key={status} className="flex items-center gap-1.5 text-meta text-ink-700">
          <span
            aria-hidden="true"
            className={cn('size-2.5 rounded-full', LEGEND_DOT_CLASSES[status])}
          />
          {JOB_STATUS_LABELS[status]}
        </li>
      ))}
      <li className="flex items-center gap-1.5 text-meta text-ink-700">
        <Star aria-hidden="true" className="size-3.5 fill-honey-400 text-status-pending-text" />
        High priority
      </li>
    </ul>
  );
}

/**
 * One job as a calendar chip. Draggable where a reschedule is possible;
 * Enter opens the same reschedule dialog, so the keyboard reaches everything
 * the mouse does. Middle-of-the-road sizes are resisted: the chip is small,
 * the hit area is not (44px rule).
 */
function JobChip({
  job,
  onReschedule,
  compact,
}: {
  job: JobListItem;
  onReschedule: (job: JobListItem) => void;
  compact?: boolean;
}) {
  const navigate = useNavigate();
  const movable = job.status === 'pending' || job.status === 'scheduled';

  function onDragStart(event: DragEvent) {
    event.dataTransfer.setData('application/x-honeycomb-job', job.id);
    event.dataTransfer.effectAllowed = 'move';
  }

  function onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      void navigate(`/admin/jobs/${job.id}`);
    }
    if ((event.key === 'r' || event.key === 'R') && movable) {
      event.preventDefault();
      onReschedule(job);
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      draggable={movable}
      onDragStart={movable ? onDragStart : undefined}
      onClick={() => {
        void navigate(`/admin/jobs/${job.id}`);
      }}
      onKeyDown={onKeyDown}
      title={`${job.jobNumber} — ${job.clientName}. Enter opens it${movable ? '; R reschedules' : ''}.`}
      className={cn(
        'flex w-full cursor-pointer items-center gap-1 rounded-sm px-1.5 text-left text-meta',
        'min-h-[28px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-honey-700',
        CHIP_CLASSES[job.status],
        movable && 'cursor-grab active:cursor-grabbing',
      )}
    >
      {job.isHighPriority ? (
        <Star
          aria-hidden="true"
          className="size-3 shrink-0 fill-honey-400 text-status-pending-text"
        />
      ) : null}
      <span className="truncate font-medium">
        {formatWallTime(job.scheduledTime).replace(' ', '')} {job.clientName}
      </span>
      {!compact && (
        <span className="sr-only">
          {job.jobNumber}, {job.statusLabel}
          {job.isHighPriority ? ', high priority' : ''}
        </span>
      )}
    </div>
  );
}

/** Month and week share the drop-target day cell; only the shape differs. */
function GridView({
  view,
  days,
  anchor,
  today,
  byDay,
  onCreate,
  onReschedule,
}: {
  view: 'month' | 'week';
  days: string[];
  anchor: string;
  today: string;
  byDay: Map<string, JobListItem[]>;
  onCreate: (date: string) => void;
  onReschedule: (job: JobListItem) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  // Called straight from the drop handler rather than through a mutation
  // hook — the hook wants the job id up front, and the id only arrives with
  // the drop. Same endpoint, same invalidation, same toasts.
  async function moveJob(job: JobListItem | undefined, date: string) {
    if (!job || job.scheduledDate === date) return;
    try {
      await api.post(`/api/admin/jobs/${job.id}/reschedule`, {
        scheduledDate: date,
        scheduledTime: job.scheduledTime,
      });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'jobs'] });
      toast.success(`${job.jobNumber} moved to ${formatDay(date)}.`);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Could not move the job.');
    }
  }

  const allJobs = [...byDay.values()].flat();

  return (
    <div className="overflow-hidden rounded-lg border border-ink-300 bg-white shadow-card">
      <div className="grid grid-cols-7 border-b border-ink-100">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className="px-2 py-2 text-center text-meta font-medium text-ink-500">
            <span className="hidden sm:inline">{label.slice(0, 3)}</span>
            <span className="sm:hidden">{label.slice(0, 1)}</span>
          </div>
        ))}
      </div>

      <div className={cn('grid grid-cols-7', view === 'month' && 'grid-rows-6')}>
        {days.map((day) => {
          const inMonth = view === 'week' || sameMonth(day, anchor);
          const dayJobs = byDay.get(day) ?? [];

          return (
            <div
              key={day}
              onDragOver={(event) => {
                event.preventDefault();
                setDropTarget(day);
              }}
              onDragLeave={() => {
                setDropTarget((current) => (current === day ? null : current));
              }}
              onDrop={(event) => {
                event.preventDefault();
                setDropTarget(null);
                const jobId = event.dataTransfer.getData('application/x-honeycomb-job');
                if (jobId !== '') {
                  void moveJob(
                    allJobs.find((entry) => entry.id === jobId),
                    day,
                  );
                }
              }}
              className={cn(
                'min-h-28 border-b border-r border-ink-100 p-1.5 align-top',
                view === 'week' && 'min-h-48',
                !inMonth && 'bg-paper-50',
                dropTarget === day &&
                  'bg-honey-50 outline outline-2 -outline-offset-2 outline-honey-500',
              )}
            >
              <div className="mb-1 flex items-center justify-between">
                <button
                  type="button"
                  aria-label={`Create a job on ${formatDay(day)}`}
                  onClick={() => {
                    onCreate(day);
                  }}
                  className={cn(
                    'flex min-h-touch min-w-touch items-center justify-center rounded-full text-meta hover:bg-honey-50',
                    day === today
                      ? 'font-bold text-bark-700'
                      : inMonth
                        ? 'text-ink-900'
                        : 'text-ink-500',
                  )}
                >
                  {Number(day.slice(8, 10))}
                </button>
              </div>

              <div className="space-y-1">
                {dayJobs.map((job) => (
                  <JobChip key={job.id} job={job} onReschedule={onReschedule} compact />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** The working day, hour by hour. Clicking an hour creates a job at that hour. */
function DayView({
  day,
  jobs,
  onCreate,
  onReschedule,
}: {
  day: string;
  jobs: JobListItem[];
  onCreate: (date: string, time: string) => void;
  onReschedule: (job: JobListItem) => void;
}) {
  const hours = Array.from({ length: 13 }, (_, index) => index + 7); // 07:00–19:00

  return (
    <div className="overflow-hidden rounded-lg border border-ink-300 bg-white shadow-card">
      {hours.map((hour) => {
        const slot = `${String(hour).padStart(2, '0')}:00`;
        const slotJobs = jobs.filter((job) => Number(job.scheduledTime.slice(0, 2)) === hour);

        return (
          <div key={hour} className="flex border-b border-ink-100 last:border-b-0">
            <button
              type="button"
              aria-label={`Create a job on ${formatDay(day)} at ${formatWallTime(slot)}`}
              onClick={() => {
                onCreate(day, slot);
              }}
              className="w-20 shrink-0 border-r border-ink-100 px-2 py-3 text-right text-meta text-ink-500 hover:bg-honey-50"
            >
              {formatWallTime(slot)}
            </button>
            <div className="min-h-touch flex-1 space-y-1 p-1.5">
              {slotJobs.map((job) => (
                <div key={job.id} className="max-w-md">
                  <JobChip job={job} onReschedule={onReschedule} />
                  <p className="mt-0.5 pl-1.5 text-meta text-ink-500">
                    {job.serviceTypeName} · {formatDuration(job.estimatedDurationMinutes)} ·{' '}
                    {job.addressSnapshot}
                  </p>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** The next two weeks as a list — the phone-friendly view. */
function AgendaView({
  from,
  to,
  byDay,
  onCreate,
  onReschedule,
}: {
  from: string;
  to: string;
  byDay: Map<string, JobListItem[]>;
  onCreate: (date: string) => void;
  onReschedule: (job: JobListItem) => void;
}) {
  const days = [];
  for (let day = from; day <= to; day = addDays(day, 1)) days.push(day);

  const hasAny = days.some((day) => (byDay.get(day) ?? []).length > 0);

  if (!hasAny) {
    return (
      <div className="rounded-lg border border-ink-300 bg-white p-6 text-center shadow-card">
        <p className="text-body text-ink-700">Nothing scheduled in these two weeks.</p>
        <Button
          className="mt-3"
          onClick={() => {
            onCreate(from);
          }}
        >
          <CalendarPlus aria-hidden="true" />
          Create job
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {days.map((day) => {
        const dayJobs = byDay.get(day) ?? [];
        if (dayJobs.length === 0) return null;

        return (
          <section
            key={day}
            className="overflow-hidden rounded-lg border border-ink-300 bg-white shadow-card"
          >
            <h3 className="bg-paper-50 border-b border-ink-100 px-4 py-2 text-label text-ink-900">
              {formatDay(day)}
            </h3>
            <ul className="divide-y divide-ink-100">
              {dayJobs.map((job) => (
                <li key={job.id} className="flex items-center gap-3 px-4 py-2.5">
                  <span className="w-20 shrink-0 text-meta text-ink-500">
                    {formatWallTime(job.scheduledTime)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <JobChip job={job} onReschedule={onReschedule} />
                  </div>
                  <span className="hidden text-meta text-ink-500 sm:block">
                    {formatDuration(job.estimatedDurationMinutes)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
